import {
  DestroyRef,
  type EnvironmentProviders,
  computed,
  inject,
  provideEnvironmentInitializer,
  signal,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { TopProgressService } from './top-progress.service';

/**
 * Feeds the router into the top progress bar: every navigation (guards, resolvers, lazy `loadChildren` /
 * `loadComponent`) and every lazy route config load counts, with no per-route code. Provided once, by the app.
 */
export function provideTopProgress(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    const router = inject(Router);
    const routeConfigLoads = signal(0);
    const subscription = router.events.subscribe((event) => {
      if (event instanceof RouteConfigLoadStart) {
        routeConfigLoads.update((count) => count + 1);
      } else if (event instanceof RouteConfigLoadEnd) {
        routeConfigLoads.update((count) => Math.max(0, count - 1));
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        // A chunk that fails to load never emits RouteConfigLoadEnd; the navigation's end settles it.
        routeConfigLoads.set(0);
      }
    });
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());
    inject(TopProgressService).watch(computed(() => router.currentNavigation() !== null || routeConfigLoads() > 0));
  });
}
